import React, { useEffect, useRef, useState } from 'react';
import { X, Download, ExternalLink } from 'lucide-react';
import api from '../api/client';
import { etichettaTipo, nomeDownload } from '../utils/documentiDipendente';

// Anteprima a video di un documento collaboratore (PDF nel visualizzatore del browser, immagini come <img>).
// `endpoint` = rotta di download già autorizzata (staff o /me), così i permessi restano quelli del server.
const MIME = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png' };
const estensione = (d) => (d.file_nome || '').split('.').pop().toLowerCase();
export const haAnteprima = (d) => !!MIME[estensione(d)];

export default function AnteprimaDocumento({ doc, endpoint, onClose, azioni = null, avviso = null }) {
  const [url, setUrl]       = useState(null);
  const [errore, setErrore] = useState(null);
  const ext = estensione(doc);
  const chiudi = useRef(onClose); chiudi.current = onClose; // evita di riscaricare il file a ogni render del genitore

  useEffect(() => {
    let creato = null;
    api.get(endpoint, { responseType: 'blob' })
      .then(r => { creato = URL.createObjectURL(new Blob([r.data], { type: MIME[ext] || 'application/octet-stream' })); setUrl(creato); })
      .catch(async e => {
        // con responseType blob anche il messaggio d'errore del server arriva come blob
        let msg = null;
        try { const t = await e.response?.data?.text?.(); msg = t ? JSON.parse(t).error : null; } catch { /* non JSON */ }
        setErrore(msg || (e.response?.status === 404 ? 'Documento non trovato' : (e.message || 'Errore')));
      });
    const esc = (e) => { if (e.key === 'Escape') chiudi.current(); };
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('keydown', esc); if (creato) URL.revokeObjectURL(creato); };
  }, [endpoint, ext]);

  const scarica = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url; a.download = nomeDownload(doc); a.click();
  };

  return (
    <div style={s.overlay} onClick={e => e.stopPropagation()}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <div style={{ minWidth: 0 }}>
            <div style={s.title}>{doc.nome}</div>
            <div style={s.sub}>{doc.tipo ? `${etichettaTipo(doc.tipo)} · ` : ''}{doc.anno ? `${doc.anno} · ` : ''}{new Date(doc.created_at || Date.now()).toLocaleDateString('it-IT')}</div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            {url && <button style={s.btn} onClick={() => window.open(url, '_blank')} title="Apri in una nuova scheda"><ExternalLink size={13} /></button>}
            {url && <button style={s.btn} onClick={scarica} title="Scarica"><Download size={13} /></button>}
            {azioni}
            <button style={s.btn} onClick={onClose} title="Chiudi"><X size={14} /></button>
          </div>
        </div>
        {avviso && <div style={{ padding: '8px 16px', fontSize: 'var(--font-size-xs)', background: '#FDF1EA', color: '#9A3A06', borderBottom: '1px solid var(--border,#1E1E1E)' }}>{avviso}</div>}
        <div style={s.body}>
          {errore ? <div style={s.msg}>Impossibile aprire l'anteprima: {errore}</div>
            : !url ? <div style={s.msg}>Caricamento anteprima...</div>
            : ext === 'pdf' ? <iframe src={url} title={`Anteprima ${doc.nome}`} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />
            : <img src={url} alt={doc.nome} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }} />}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '16px' },
  modal:   { background: 'var(--bg)', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '12px', width: '100%', maxWidth: '960px', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: '1px solid var(--border,#1E1E1E)' },
  title:   { fontSize: 'var(--font-size-sm)', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub:     { fontSize: 'var(--font-size-xs)', color: '#777' },
  body:    { flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-input,#1A1A1A)' },
  msg:     { color: '#777', fontSize: 'var(--font-size-sm)', padding: '40px', textAlign: 'center' },
  btn:     { display: 'flex', alignItems: 'center', background: 'var(--bg-card,#141414)', border: '1px solid var(--border2,#2A2A2A)', color: 'var(--text)', borderRadius: '7px', padding: '7px 10px', cursor: 'pointer' },
};
